import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  ActivityLogsService,
  PropertyHistoryLog,
  PropertyHistoryParams,
} from "src/app/core/services/logs.service";
import {
  HISTORY_CATEGORY_ICON,
  HISTORY_TYPE_FILTERS,
  HistoryCategory,
  HistoryChangeVm,
  buildChangeVms,
  categoryOf,
  formatFileSize,
  initialsOf,
  isAuditV2,
} from "./property-history.format";

interface HistoryFileVm {
  name: string;
  kind: string;
  typeName: string | null;
  size: string | null;
}

interface HistoryItemVm {
  id: number;
  action: string;
  createdAt: Date;
  category: HistoryCategory;
  icon: string;
  time: string;
  ok: boolean;
  source: string | null;
  actorName: string;
  actorInitials: string;
  actorEmail: string | null;
  actorRole: string | null;
  // Nome (ou email) do MASTER que fez a ação em "Ver como"; null fora disso.
  impersonatorName: string | null;
  title: string;
  titleParams: Record<string, any>;
  changes: HistoryChangeVm[];
  highlight: HistoryChangeVm | null;
  file: HistoryFileVm | null;
  previousFile: HistoryFileVm | null;
  moves: Array<{ name: string | null; from: number; to: number }>;
  notes: Array<{ key: string; params?: Record<string, any>; text?: string }>;
  correlationId: string | null;
  expanded: boolean;
}

interface HistoryDayVm {
  key: string;
  label: string;
  items: HistoryItemVm[];
}

const LOCALE_BY_LANG: Record<string, string> = {
  pt: "pt-PT",
  en: "en-GB",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
};

/** Alterações mostradas sem expandir; as restantes ficam atrás de "ver todas". */
const VISIBLE_CHANGES = 4;

@Component({
  selector: "app-property-history",
  templateUrl: "./property-history.component.html",
  styleUrls: ["./property-history.component.scss"],
})
export class PropertyHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() propertyId!: string;

  readonly visibleChanges = VISIBLE_CHANGES;
  loading = false;
  loadError = false;
  exporting = false;
  days: HistoryDayVm[] = [];
  total = 0;
  page = 1;
  pageSize = 10;
  actors: Array<{ id: string; name: string; email?: string | null }> = [];

  searchControl = new FormControl("");
  period: string = "all";
  typeFilter: string | null = null;
  userId: string | null = null;
  status: "success" | "error" | null = null;
  dateFrom: string | null = null;
  dateTo: string | null = null;
  showFilters = false;

  readonly periodOptions = [
    { value: "all", label: "t-property-history-period-all" },
    { value: "today", label: "t-property-history-period-today" },
    { value: "7d", label: "t-property-history-period-7d" },
    { value: "30d", label: "t-property-history-period-30d" },
    { value: "90d", label: "t-property-history-period-90d" },
    { value: "custom", label: "t-property-history-period-custom" },
  ];
  readonly typeOptions = HISTORY_TYPE_FILTERS;
  readonly statusOptions = [
    { value: "success", label: "t-property-history-result-success" },
    { value: "error", label: "t-property-history-result-error" },
  ];

  private subscriptions = new Subscription();
  private request?: Subscription;

  constructor(
    private activityLogsService: ActivityLogsService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchControl.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(() => this.reload())
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["propertyId"] && this.propertyId) {
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.request?.unsubscribe();
  }

  get activeFiltersCount(): number {
    return [this.userId, this.status].filter(Boolean).length;
  }

  get shownCount(): number {
    return this.days.reduce((total, day) => total + day.items.length, 0);
  }

  reload(): void {
    this.page = 1;
    this.load();
  }

  onPeriodChange(): void {
    if (this.period !== "custom") {
      this.dateFrom = null;
      this.dateTo = null;
    }
    this.reload();
  }

  clearFilters(): void {
    this.userId = null;
    this.status = null;
    this.period = "all";
    this.dateFrom = null;
    this.dateTo = null;
    this.typeFilter = null;
    this.searchControl.setValue("", { emitEvent: false });
    this.reload();
  }

  load(): void {
    if (!this.propertyId) return;

    this.loading = true;
    this.loadError = false;
    this.request?.unsubscribe();
    this.request = this.activityLogsService
      .getPropertyHistory(this.propertyId, this.buildParams())
      .subscribe({
        next: (response) => {
          this.total = response?.total ?? 0;
          if (response?.actors) this.actors = response.actors;
          this.days = this.groupByDay(
            (response?.items ?? []).map((log) => this.toItemVm(log))
          );
          this.loading = false;
        },
        error: () => {
          this.days = [];
          this.total = 0;
          this.loadError = true;
          this.loading = false;
        },
      });
  }

  exportCsv(): void {
    if (!this.propertyId || this.exporting) return;
    this.exporting = true;
    this.activityLogsService
      .exportPropertyHistory(this.propertyId, this.buildParams())
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `historico-imovel-${this.propertyId}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
          this.exporting = false;
        },
        error: () => (this.exporting = false),
      });
  }

  toggle(item: HistoryItemVm): void {
    item.expanded = !item.expanded;
  }

  trackById(_: number, item: HistoryItemVm): number {
    return item.id;
  }

  trackByKey(_: number, day: HistoryDayVm): string {
    return day.key;
  }

  // ---------------------------------------------------------------------------

  private get locale(): string {
    const lang = (this.translate.currentLang || "pt").slice(0, 2);
    return LOCALE_BY_LANG[lang] ?? "pt-PT";
  }

  private buildParams(): PropertyHistoryParams {
    const { dateFrom, dateTo } = this.resolvePeriod();
    return {
      page: this.page,
      pageSize: this.pageSize,
      dateFrom,
      dateTo,
      actions:
        this.typeOptions.find((option) => option.value === this.typeFilter)
          ?.actions ?? null,
      userId: this.userId,
      status: this.status,
      search: this.searchControl.value,
    };
  }

  private resolvePeriod(): { dateFrom: string | null; dateTo: string | null } {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysAgo = (days: number) =>
      new Date(startOfDay.getTime() - days * 86400000).toISOString();

    switch (this.period) {
      case "today":
        return { dateFrom: startOfDay.toISOString(), dateTo: null };
      case "7d":
        return { dateFrom: daysAgo(6), dateTo: null };
      case "30d":
        return { dateFrom: daysAgo(29), dateTo: null };
      case "90d":
        return { dateFrom: daysAgo(89), dateTo: null };
      case "custom":
        return {
          dateFrom: this.dateFrom
            ? new Date(`${this.dateFrom}T00:00:00`).toISOString()
            : null,
          dateTo: this.dateTo
            ? new Date(`${this.dateTo}T23:59:59.999`).toISOString()
            : null,
        };
      default:
        return { dateFrom: null, dateTo: null };
    }
  }

  private toItemVm(log: PropertyHistoryLog): HistoryItemVm {
    const metadata = log.metadata ?? {};
    const translateFn = (key: string, params?: Record<string, any>) =>
      this.translate.instant(key, params);
    const category = categoryOf(log.action, metadata);
    const changes = buildChangeVms(metadata, translateFn, this.locale);
    const actor = metadata.actor ?? metadata.changedBy ?? null;
    const actorName =
      log.user?.name ??
      actor?.name ??
      log.createdBy ??
      this.translate.instant(
        log.source === "SYSTEM" || !log.user
          ? "t-property-history-actor-system"
          : "t-property-history-actor-unknown"
      );

    const createdAt = new Date(log.createdAt);
    const single =
      changes.length === 1 && !changes[0].details.length && !changes[0].longText
        ? changes[0]
        : null;

    return {
      id: log.id,
      action: log.action,
      createdAt,
      category,
      icon: HISTORY_CATEGORY_ICON[category],
      time: createdAt.toLocaleTimeString(this.locale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
      ok: log.status !== false,
      source: log.source ?? null,
      actorName,
      actorInitials: initialsOf(actorName),
      actorEmail: log.user?.email ?? actor?.email ?? log.createdBy ?? null,
      actorRole: actor?.role ?? null,
      impersonatorName:
        log.impersonator?.name || log.impersonator?.email || null,
      ...this.buildTitle(log.action, changes),
      changes,
      highlight: single,
      file: this.toFileVm(metadata.file),
      previousFile: this.toFileVm(metadata.previousFile),
      moves: Array.isArray(metadata.moves) ? metadata.moves : [],
      notes: this.buildNotes(log, metadata),
      correlationId: log.correlationId ?? null,
      expanded: false,
    };
  }

  private buildTitle(
    action: string,
    changes: HistoryChangeVm[]
  ): { title: string; titleParams: Record<string, any> } {
    if (action === "PROPERTY_UPDATE" && changes.length === 1) {
      return {
        title: "t-property-history-title-field",
        titleParams: { field: this.translate.instant(changes[0].label) },
      };
    }
    if (action === "PROPERTY_UPDATE" && changes.length > 1) {
      return {
        title: "t-property-history-title-fields",
        titleParams: { count: changes.length },
      };
    }
    return { title: `t-activity-action-${action}`, titleParams: {} };
  }

  private toFileVm(file: any): HistoryFileVm | null {
    if (!file) return null;
    return {
      name: file.name ?? file.fileName ?? `#${file.id}`,
      kind: file.kind ?? "other",
      typeName: file.documentTypeName ?? null,
      size: formatFileSize(file.size, this.locale),
    };
  }

  /** Contexto extra legível (motivo, duplicado de/para, falha…). */
  private buildNotes(
    log: PropertyHistoryLog,
    metadata: any
  ): Array<{ key: string; params?: Record<string, any>; text?: string }> {
    const notes: Array<{ key: string; params?: Record<string, any>; text?: string }> = [];

    if (metadata.message) {
      notes.push({ key: "t-property-history-note-message", text: metadata.message });
    }
    if (metadata.excerpt) {
      notes.push({ key: "t-property-history-note-excerpt", text: metadata.excerpt });
    }
    if (metadata.duplicatedTo?.reference) {
      notes.push({
        key: "t-property-history-note-duplicated-to",
        params: { reference: metadata.duplicatedTo.reference },
      });
    }
    if (metadata.duplicatedFrom?.id && log.action === "PROPERTY_DUPLICATE") {
      notes.push({ key: "t-property-history-note-duplicated-from" });
    }
    if (metadata.resaleTo?.reference) {
      notes.push({
        key: "t-property-history-note-resale-to",
        params: { reference: metadata.resaleTo.reference },
      });
    }
    if (metadata.resaleOf?.id && log.action === "PROPERTY_RESALE") {
      notes.push({
        key: "t-property-history-note-resale-from",
        params: { reference: metadata.resaleOf.reference ?? "" },
      });
    }
    if (metadata.propagatedFrom?.id) {
      notes.push({ key: "t-property-history-note-propagated" });
    }
    if (metadata.delivery) {
      notes.push({ key: `t-property-history-delivery-${metadata.delivery}` });
    }
    if (metadata.context && log.action === "PROPERTY_VIEW") {
      notes.push({ key: `t-property-history-context-${metadata.context}` });
    }
    if (metadata.deletedProperty?.reference) {
      notes.push({
        key: "t-property-history-note-deleted",
        params: {
          reference: metadata.deletedProperty.reference,
          files: metadata.deletedProperty.files?.length ?? 0,
        },
      });
    }
    if (log.status === false) {
      notes.push({
        key: "t-property-history-note-failed",
        text: metadata.error?.message ?? metadata.reason ?? undefined,
      });
    }
    if (!isAuditV2(metadata) && metadata.reference && !notes.length && !metadata.changes?.length) {
      notes.push({ key: "t-reference", text: metadata.reference });
    }

    return notes;
  }

  private groupByDay(items: HistoryItemVm[]): HistoryDayVm[] {
    const days: HistoryDayVm[] = [];
    const today = this.dayKey(new Date());
    const yesterday = this.dayKey(new Date(Date.now() - 86400000));

    items.forEach((item) => {
      const date = item.createdAt;
      const key = this.dayKey(date);
      let day = days.find((d) => d.key === key);
      if (!day) {
        const formatted = date.toLocaleDateString(this.locale, {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        const prefix =
          key === today
            ? this.translate.instant("t-property-history-today")
            : key === yesterday
            ? this.translate.instant("t-property-history-yesterday")
            : null;
        day = { key, label: prefix ? `${prefix}, ${formatted}` : formatted, items: [] };
        days.push(day);
      }
      day.items.push(item);
    });

    return days;
  }

  private dayKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
